/**
 * Turning a leg specification into tradeable instruments — ROADMAP 5.2.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PURE, AND THAT IS THE WHOLE POINT
 *
 * Spot, step, lots and the chain are all ARGUMENTS. Nothing here reads a tick
 * store, a database or a clock, so "Iron Condor at spot 22,613" is a test
 * that runs in under a millisecond with no feed and no broker — and the
 * browser's preview and the server's order are computed by one function
 * rather than two that look alike. If the preview says 22,050 and the order
 * goes at 22,100, the user traded something they did not agree to.
 *
 * IT TAKES LEGS, NOT A TEMPLATE
 *
 * A trader edits their own copy of a strategy (5.19) and may override a
 * criterion for a single placement (5.7). A resolver that reached into
 * `template.legs` would force every override to either mutate the shared
 * catalogue — which is global and would change the default for everyone in
 * the process — or be bolted on afterwards at each call site.
 *
 * IT FAILS AS A WHOLE
 *
 * Three legs of a four-leg condor is not "most of a condor". It is a naked
 * short, which is the position ROADMAP 5.10 exists to clean up after. So a
 * leg that cannot be resolved fails the basket, and EVERY unresolvable leg is
 * named in one pass — telling the user about the first one and making them
 * click again to discover the second is a worse version of the same answer.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { INTENT_LIMITS } from './orderIntentWire.js';
/**
 * Strikes are compared by equality against the chain, so both sides must be
 * rounded identically.
 *
 * Index steps are whole numbers, but stock option steps can be 2.50 or 0.50,
 * and `atm + steps * step` on those can land on 247.50000000000003. A strike
 * off by 3e-14 matches nothing, and the leg fails for a reason no one would
 * ever guess from the message.
 */
/** Index-cache key for the strategy's own chain, which is never a kind. */
const STRATEGY_CHAIN = '@strategy';
/**
 * How a kind reads in a refusal a trader has to act on.
 *
 * `NEXT_MONTHLY` in a sentence is the enum leaking; the trader picked "Next
 * month" in the form and that is the phrase that points them back at it.
 */
const EXPIRY_KIND_WORD = {
    WEEKLY: 'this week',
    NEXT_WEEKLY: 'next week',
    MONTHLY: 'this month',
    NEXT_MONTHLY: 'next month',
};
const roundStrike = (v) => Math.round(v * 10_000) / 10_000;
const quoteKey = (strike, optionType) => `${roundStrike(strike)}|${optionType}`;
/**
 * The strike nearest spot.
 *
 * TIES GO UP: `Math.round` rounds .5 toward positive infinity, so spot 22,625
 * with a step of 50 resolves to 22,650 rather than 22,600. The choice is
 * arbitrary; having it written down and tested is not, because preview and
 * order must agree and a silent disagreement is a trade the user did not
 * authorise.
 */
export function atmStrikeFor(spot, step) {
    return roundStrike(Math.round(spot / step) * step);
}
export function resolveLegs(legs, ctx) {
    const bad = validateContext(legs, ctx);
    if (bad)
        return { ok: false, why: bad, unresolved: [] };
    const atm = atmStrikeFor(ctx.spot, ctx.step);
    /*
     * Indexed once PER EXPIRY rather than per leg: a four-leg condor against a
     * 500-row chain is 4 scans otherwise, for no reason. Built lazily, so a
     * strategy with no calendar leg indexes exactly one chain as before.
     */
    const indexes = new Map();
    const indexOf = (key, quotes) => {
        let idx = indexes.get(key);
        if (idx)
            return idx;
        const built = new Map();
        for (const q of quotes)
            built.set(quoteKey(q.strike, q.optionType), q);
        indexes.set(key, built);
        idx = built;
        return idx;
    };
    const resolved = [];
    const unresolved = [];
    for (const [seq, leg] of legs.entries()) {
        const chain = chainForLeg(leg, ctx);
        if (!chain.ok) {
            unresolved.push({ seq, optionType: leg.optionType, why: chain.why });
            continue;
        }
        const found = pick(leg.strikeCriteria, leg.optionType, atm, ctx, chain.quotes, indexOf(chain.key, chain.quotes));
        if (!found.ok) {
            unresolved.push({ seq, optionType: leg.optionType, why: found.why });
            continue;
        }
        resolved.push({
            token: found.quote.token,
            side: leg.side,
            optionType: leg.optionType,
            strike: found.quote.strike,
            lots: ctx.lots * leg.ratio,
            ltp: found.quote.ltp,
            ...(found.quote.bid === undefined ? {} : { bid: found.quote.bid }),
            ...(found.quote.ask === undefined ? {} : { ask: found.quote.ask }),
        });
    }
    if (unresolved.length > 0) {
        return {
            ok: false,
            why: `${unresolved.length} of ${legs.length} leg(s) could not be resolved. Nothing was built.`,
            unresolved,
        };
    }
    return collapse(resolved, atm);
}
function pick(criteria, optionType, atm, ctx, chain, byStrike) {
    switch (criteria.kind) {
        case 'ATM_OFFSET': {
            const strike = roundStrike(atm + criteria.steps * ctx.step);
            const quote = byStrike.get(quoteKey(strike, optionType));
            if (!quote) {
                /*
                 * Arithmetically correct and not tradeable. The exchange does not
                 * list every multiple of the step — far strikes appear as spot moves
                 * toward them, and some get restricted (exchange code 16387). This
                 * is the common failure for a wide condor on a quiet underlying.
                 */
                return { ok: false, why: `${strike} ${optionType} is not listed for this expiry` };
            }
            return { ok: true, quote };
        }
        case 'CLOSEST_PREMIUM':
            return nearest(chain, optionType, atm, (q) => Math.abs(q.ltp - criteria.premium), 'premium');
        case 'PREMIUM_RANGE':
            return within(chain, optionType, atm, (q) => q.ltp, criteria.min, criteria.max, `a premium between ₹${criteria.min} and ₹${criteria.max}`);
        case 'CLOSEST_DELTA': {
            const delta = deltaReader(ctx);
            if (!delta.ok)
                return delta;
            return nearest(chain, optionType, atm, (q) => {
                const d = delta.read(q.token);
                return d === null ? null : Math.abs(Math.abs(d) - criteria.delta);
            }, 'delta');
        }
        case 'DELTA_RANGE': {
            const delta = deltaReader(ctx);
            if (!delta.ok)
                return delta;
            return within(chain, optionType, atm, (q) => {
                const d = delta.read(q.token);
                return d === null ? null : Math.abs(d);
            }, criteria.min, criteria.max, `a delta between ${criteria.min} and ${criteria.max}`);
        }
    }
}
/**
 * The delta source, or the refusal to use in its place.
 *
 * NEVER FALLS BACK TO AN OFFSET. A delta criterion with no greeks is a leg
 * nobody can price the way the trader described it, and quietly substituting
 * "two strikes out" would place a contract they did not choose — at a
 * plausible strike, so nothing would look wrong.
 */
function deltaReader(ctx) {
    if (!ctx.deltaOf) {
        return { ok: false, why: 'Delta is not available here, so this leg cannot pick its strike' };
    }
    return { ok: true, read: ctx.deltaOf };
}
/**
 * Which chain a leg resolves against.
 *
 * A leg naming the strategy's own expiry is not a calendar leg, so it never
 * needs a second chain — the common case where a builder writes the expiry
 * onto every row stays free.
 */
function chainForLeg(leg, ctx) {
    /*
     * `== null` CATCHES BOTH, and that is deliberate rather than sloppy.
     * A leg from the catalogue omits the field and a leg read from the
     * database carries SQL NULL; they mean the same thing — "the strategy's
     * expiry" — and an `=== undefined` guard refused every stored leg while
     * passing every test built from a literal.
     */
    const own = leg.expiryKind;
    if (own == null || ctx.chainKinds?.includes(own)) {
        return { ok: true, key: STRATEGY_CHAIN, quotes: ctx.chain };
    }
    const quotes = ctx.chainByExpiry?.get(own);
    if (!quotes || quotes.length === 0) {
        return { ok: false, why: `this leg wants the ${EXPIRY_KIND_WORD[own]} expiry, which is not loaded` };
    }
    return { ok: true, key: own, quotes };
}
/**
 * Candidate filtering, shared by every criterion that SCANS.
 *
 * A strike with no price is never a candidate. Zero is not "cheap" — it is
 * "has not traded", and an option genuinely worth five paise reports 0.05.
 * Matching on it would pick the deadest contract on the board, which is also
 * the one that cannot be filled.
 */
function candidates(chain, optionType) {
    return chain.filter((q) => q.optionType === optionType && q.ltp > 0);
}
/**
 * The candidate with the smallest score — a SCAN, not arithmetic.
 *
 * This is the half of the union that cannot be computed from spot alone, and
 * the reason `ResolveContext` carries the whole chain rather than a lookup.
 * A `null` score means the measure could not be taken for that contract —
 * an unsolvable delta, most often — and it is skipped rather than sorted to
 * one end.
 *
 * TIES BREAK TOWARD ATM, THEN TOWARD THE LOWER STRIKE. Two strikes are
 * genuinely equidistant often enough — a flat smile does it for premium and
 * a symmetric surface for delta — and without a rule the winner would depend
 * on chain ordering, which is neither something the caller controls nor
 * something the trader can predict. Nearer ATM is the more liquid of the two.
 */
function nearest(chain, optionType, atm, score, measure) {
    let best;
    let bestScore = Number.POSITIVE_INFINITY;
    let bestFromAtm = Number.POSITIVE_INFINITY;
    const pool = candidates(chain, optionType);
    let measured = 0;
    for (const q of pool) {
        const value = score(q);
        if (value === null)
            continue;
        measured += 1;
        const fromAtm = Math.abs(q.strike - atm);
        const better = value < bestScore ||
            (value === bestScore &&
                (fromAtm < bestFromAtm || (fromAtm === bestFromAtm && q.strike < (best?.strike ?? 0))));
        if (better) {
            best = q;
            bestScore = value;
            bestFromAtm = fromAtm;
        }
    }
    if (!best)
        return { ok: false, why: emptyWhy(optionType, pool.length, measured, measure) };
    return { ok: true, quote: best };
}
/**
 * The candidate INSIDE a band that sits nearest the money.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * AN EMPTY BAND IS AN ANSWER, NOT A NEAR MISS
 *
 * This is the whole difference from `nearest`, and the reason a range is its
 * own criterion rather than a wider target. "Nearest ₹10" on a chain whose
 * cheapest wing is ₹40 returns the ₹40 — correct by its own rule and not
 * what the trader meant. "Between ₹8 and ₹12" returns nothing, and that
 * nothing is the signal: the hedge they costed does not exist today.
 *
 * Nearest the money among those that qualify, rather than cheapest or
 * furthest, for the same liquidity reason the tie-break above gives.
 * ─────────────────────────────────────────────────────────────────────────
 */
function within(chain, optionType, atm, measure, min, max, wanted) {
    let best;
    let bestFromAtm = Number.POSITIVE_INFINITY;
    const pool = candidates(chain, optionType);
    let measured = 0;
    for (const q of pool) {
        const value = measure(q);
        if (value === null)
            continue;
        measured += 1;
        if (value < min || value > max)
            continue;
        const fromAtm = Math.abs(q.strike - atm);
        if (fromAtm < bestFromAtm || (fromAtm === bestFromAtm && q.strike < (best?.strike ?? 0))) {
            best = q;
            bestFromAtm = fromAtm;
        }
    }
    if (!best) {
        if (measured === 0)
            return { ok: false, why: emptyWhy(optionType, pool.length, 0, wanted) };
        return { ok: false, why: `no ${optionType} strike on this expiry has ${wanted}` };
    }
    return { ok: true, quote: best };
}
/** Why a scan found nothing — the two causes read very differently. */
function emptyWhy(optionType, priced, measured, wanted) {
    if (priced === 0)
        return `no ${optionType} strike on this expiry has a price yet`;
    if (measured === 0)
        return `no ${optionType} strike on this expiry has a solved ${wanted}`;
    return `no ${optionType} strike could be matched on ${wanted}`;
}
// ─── the checks that are not about one leg ───────────────────────────────────
function validateContext(legs, ctx) {
    if (legs.length === 0)
        return 'A strategy with no legs cannot be resolved.';
    if (!(ctx.step > 0))
        return `Strike step must be positive, got ${ctx.step}.`;
    if (!(ctx.spot > 0))
        return `Spot must be positive, got ${ctx.spot}.`;
    if (!Number.isInteger(ctx.lots) || ctx.lots <= 0) {
        return `Lots must be a positive whole number, got ${ctx.lots}.`;
    }
    if (ctx.chain.length === 0)
        return 'The option chain is empty; nothing can be resolved.';
    for (const [seq, leg] of legs.entries()) {
        const lots = ctx.lots * leg.ratio;
        if (lots > INTENT_LIMITS.maxLots) {
            /*
             * Caught here rather than at the order boundary. A ratio leg fails
             * before its partner does — a 1:2 spread at 6,000 lots passes on the
             * long and fails on the short — and discovering that halfway through
             * placing is the half-sent basket §5.5 exists to prevent.
             */
            return `Leg ${seq + 1} would be ${lots} lots, over the ${INTENT_LIMITS.maxLots} the order path accepts.`;
        }
    }
    return null;
}
/**
 * Merge or refuse legs that landed on the same contract.
 *
 * Impossible from the seed catalogue, whose templates are checked for
 * duplicates by test — but reachable the moment a trader edits criteria
 * (5.19) or overrides them for one placement (5.7). Two criteria written
 * differently can still select the same strike.
 */
function collapse(legs, atmStrike) {
    const byToken = new Map();
    for (const leg of legs) {
        const seen = byToken.get(leg.token);
        if (!seen) {
            byToken.set(leg.token, leg);
            continue;
        }
        if (seen.side !== leg.side) {
            /*
             * BUY and SELL on one contract net to nothing, pay two sets of
             * charges, and are the textbook way to trip the exchange's self-trade
             * check ([B21]), which cancels the ACTIVE order. Refused rather than
             * cancelled out, because it means the strategy says something the
             * trader did not intend.
             */
            return {
                ok: false,
                why: `Two legs both trade ${leg.strike} ${leg.optionType} in opposite directions, which cancel out.`,
                unresolved: [],
            };
        }
        // Same side: one order for the total, never two in the same instrument.
        byToken.set(leg.token, { ...seen, lots: seen.lots + leg.lots });
    }
    return { ok: true, atmStrike, legs: [...byToken.values()] };
}
/**
 * The order the legs are SENT in — ROADMAP 5.9.
 *
 * PROTECTIVE (BUY) LEGS FIRST, always.
 *
 * If a long wing is going to be rejected — no margin, a price band, a strike
 * that is listed but not trading — we learn it while there is NOTHING in the
 * market to unwind. Send the shorts first and the same rejection finds you
 * holding naked short options, which is the position 5.10 exists to clean up
 * after and the one it cannot always clean up cheaply.
 *
 * It costs nothing: the legs are the same legs, in a different sequence.
 *
 * Within a side the input order is preserved, so a template's own leg order
 * still means something to a reader. `sort` is stable in every engine this
 * runs on, and the array is copied rather than sorted in place because the
 * caller's list is also what the preview rendered.
 */
export function orderEntryLegs(legs) {
    return [...legs].sort((a, b) => (a.side === b.side ? 0 : a.side === 'BUY' ? -1 : 1));
}
//# sourceMappingURL=strategyResolver.js.map